import { Link, useLocation } from "react-router-dom";
import logo from "../assets/images/avatar1.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleRight,
  faBars,
  faCaretDown,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState, useRef } from "react";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import { motion, AnimatePresence } from "framer-motion";
import MobileHeader from "./mobileHeader.component";
import { useUserParams } from "../hooks/useUserParams";

export default function Header() {
  const { getCurrentLogo } = useUserParams();
  const [menus, setMenus] = useState({
    buying: false,
    selling: false,
    info: false,
    locations: false,
  });
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [headerPosition, setHeaderPosition] = useState(100);
  const [headerTiming, setHeaderTiming] = useState(0.8);
  const location = useLocation();
  const [path, setPath] = useState(location.pathname);

  // Refs
  const headerRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };

  const menuVariants = {
    hidden: {
      opacity: 0,
      y: -8,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
    transition: {
      duration: 0.35,
      ease: "easeOut",
    }
  };

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  const setDropdown = (key, bool) => {
    setMenus((prevMenus) => ({
      ...prevMenus,
      ...Object.fromEntries(
        Object.keys(prevMenus).map((k) => [k, k === key ? bool : false])
      ),
    }));
  };

  const navClick = () => {
    setHeaderPosition(100);
    setHeaderTiming(0);
    setTimeout(() => {
      setHeaderTiming(0.8);
    }, 100);
    setMenus({ buying: false, selling: false, info: false, locations: false });
  };

  useEffect(() => {
    setPath(location.pathname);
  }, [location]);

  const adjustHeader = () => {
    const st = window.scrollY;
    const header = headerRef.current;

    if (!header) return;

    if (st !== 0) {
      header.classList.add("transparent-black");
    } else if (location.pathname === "/") {
      header.classList.remove("transparent-black");
    }

    if (st > lastScrollTopRef.current) {
      header.classList.add("hide-header");
    } else if (st < lastScrollTopRef.current) {
      header.classList.remove("hide-header");
    }

    lastScrollTopRef.current = st <= 0 ? 0 : st;
  };

  useEffect(() => {
    window.addEventListener("scroll", adjustHeader);
    return () => {
      window.removeEventListener("scroll", adjustHeader);
    };
  }, [location, path]);

  return (
    <>
      {windowSize > 1250 ? (
        <div
          ref={headerRef}
          style={{ gridTemplateColumns: "1fr auto " }}
          className={`duration-700 w-full top-0 py-4 px-16 grid min-h-28 content-center fixed text-summit-muted z-[100] uppercase`}
        >
          <img src={getCurrentLogo()} className="h-10 circle-mask" alt="Logo" />
          <div
            className="flex gap-x-12 items-center text-sm tracking-wider"
          >
            {constants.NAVIGATION.map((item) => {
              if (!item.children) {
                return (
                  <Link
                    key={item.title}
                    to={item.to}
                    className="pb-2 mt-2 hover:text-summit-highlight"
                  >
                    {item.title}
                  </Link>
                );
              }

              return (
                <div
                  key={item.title}
                  className="relative inline-flex items-center cursor-pointer pb-2 mt-2 hover:text-summit-highlight"
                  onMouseEnter={() => setActiveMenu(item.slug)}
                  onMouseLeave={() => setActiveMenu(null)}
                >
                  <span className="inline-flex items-center">
                    {item.title}

                    <FontAwesomeIcon
                      icon={faAngleDown}
                      className={`ml-2 duration-300 ${activeMenu === item.slug ? "rotate-180" : ""
                        }`}
                    />
                  </span>

                  <AnimatePresence>
                    {activeMenu === item.slug && (
                      <motion.div
                        initial={{
                          opacity: 0,
                          x: "-50%",
                          y: -25,
                        }}
                        animate={{
                          opacity: 1,
                          x: "-50%",
                          y: 0,
                        }}
                        exit={{
                          opacity: 0,
                          x: "-50%",
                          y: -15,
                        }}
                        transition={{
                          duration: 0.35,
                        }}
                        className="absolute left-1/2 top-full mt-3 w-[220px] bg-summit-primary shadow-lg z-20"
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.to}
                            to={child.to}
                            className="block px-4 py-3 text-center hover:bg-summit-tertiary hover:text-summit-background transition-colors"
                          >
                            {child.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <MobileHeader location={location} path={path} adjustHeader={adjustHeader} headerRef={headerRef} />
      )}
    </>
  );
}
